import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/chooseyourcareplan")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Choose Your Care Plan — Three Day Launch" },
      { name: "description", content: "Pick the website care plan that fits your business. Hosting, SSL, and edits starting at $25/mo. Upgrade or cancel anytime." },
      { property: "og:title", content: "Choose Your Care Plan — Three Day Launch" },
      { property: "og:description", content: "Pick the website care plan that fits your business. Starting at $25/mo." },
      { property: "og:url", content: "https://threedaylaunch.com/chooseyourcareplan" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/chooseyourcareplan" }],
  }),
  component: WebsiteCarePlansPage,
});

const plans = [
  {
    name: "Option 1",
    tagline: "Premium Hosting",
    price: "$25",
    cadence: "",
    features: [
      "30 Days Free Hosting",
      "Premium Website Hosting",
      "SSL Secure Certificate",
    ],
    note: "Does not include any website edits.",
    highlighted: false,
    signupUrl: "https://shop.sierralindesign.com/careplanone/",
  },
  {
    name: "Option 2",
    tagline: "Premium Hosting + One Edit Per Month",
    price: "$45",
    cadence: "",
    features: [
      "30 Days Free Hosting",
      "Premium Website Hosting",
      "SSL Secure Certificate",
      "1 website edit / change per month",
    ],
    highlighted: true,
    signupUrl: "https://shop.sierralindesign.com/careplantwo/",
  },
  {
    name: "Option 3",
    tagline: "Premium Hosting + 5 Edits Per Month",
    price: "$199",
    cadence: "",
    features: [
      "60 Days Free Hosting",
      "Premium Website Hosting",
      "SSL Secure Certificate",
      "Up to 5 changes per month",
    ],
    highlighted: false,
    signupUrl: "https://shop.sierralindesign.com/careplanthree/",
  },
];

function WebsiteCarePlansPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main>
        {/* Hero */}
        <section className="bg-secondary text-secondary-foreground pt-32 pb-20">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h1 className="mt-5 text-5xl md:text-6xl font-bold tracking-tight">
              Choose your Website <span className="text-primary">Care Plan.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70">
              Keep your custom website online, secure, and up to date. Choose the plan that fits your business. Upgrade, downgrade, or cancel anytime.
            </p>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`relative rounded-3xl p-8 border shadow-card-soft flex flex-col ${
                    plan.highlighted
                      ? "bg-card border-primary/40 ring-2 ring-primary/20"
                      : "bg-card border-border"
                  }`}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary-gradient px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground shadow-glow">
                      Most popular
                    </div>
                  )}
                  <div className="text-xs font-bold uppercase tracking-wider text-primary">{plan.name}</div>
                  <h2 className="mt-2 text-2xl font-bold">{plan.tagline}</h2>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-5xl font-bold">{plan.price}</span>
                    <span className="text-sm text-muted-foreground">{plan.cadence}</span>
                  </div>
                  <ul className="mt-8 space-y-3 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                          <Check className="h-3 w-3" />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.note && (
                    <p className="mt-6 text-xs text-muted-foreground font-bold">{plan.note}</p>
                  )}
                  <Button
                    asChild
                    className="mt-6 bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold rounded-full h-12 w-full shadow-glow"
                  >
                    <a href={plan.signupUrl}>Sign Up</a>
                  </Button>
                </div>
              ))}
            </div>

            {/* VIP callout */}
            <div className="mt-16 rounded-3xl bg-secondary text-secondary-foreground p-10 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-bold">
                Looking for more ongoing edits and support?
              </h3>
              <p className="mt-3 text-white/70 max-w-xl mx-auto">
                Get in touch to request VIP support options tailored to your business.
              </p>
              <div className="mt-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
                >
                  <Link to="/contact">
                    Request VIP support <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="mx-auto max-w-7xl px-6 py-5 flex flex-wrap items-center justify-between gap-3">
        <Link to="/" className="flex w-full sm:w-auto items-center gap-2 text-white">
          <div className="h-9 w-9 rounded-lg bg-primary-gradient flex items-center justify-center shadow-glow">
            <Rocket className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold tracking-tight text-lg">Three Day Launch<sup className="text-xs ml-0.5">®</sup></span>
        </Link>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10">
      <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
            <Rocket className="h-4 w-4 text-white" />
          </div>
          <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
        </div>
        <nav className="flex items-center gap-6 text-sm text-white/70">
          <Link to="/about" className="hover:text-white transition-colors">About</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
        </nav>
        <div className="text-sm text-white/80">© {new Date().getFullYear()} Three Day Launch. Custom websites for business owners.</div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-4 text-center text-sm text-white/80">
        Based in Chicago | Serving Clients Worldwide
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-2 text-center text-xs text-white/60">
        Three Day Launch is an independent website design studio creating professional websites.
      </div>
    </footer>
  );
}
